import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { SharedModule, StringCustomFieldConfig, FormInputComponent, NotificationService } from '@vendure/admin-ui/core';
import { DEFAULT_ACTIVE_LANGUAGE } from '../constants';
import { safeJSONParse } from '../utils';
import { QaData } from '../ui-types';
import { Observable } from 'rxjs';

@Component({
  selector: 'question-answer-input',
  template: `
    <vdr-card class="mb-2">
      <vdr-form-field [label]="'Question'" class="mb-2">
        <vdr-dynamic-form-input [control]="questionControl" [def]="textValueDef"></vdr-dynamic-form-input>
      </vdr-form-field>

      <!-- Answer Input (Rich-text Editor) -->
      <vdr-form-field [label]="'Answer'">
        <vdr-dynamic-form-input [control]="answerControl" [def]="textValueDef"></vdr-dynamic-form-input>
      </vdr-form-field>
    </vdr-card>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SharedModule],
})
export class QAInputComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit {
  @Input() readonly: boolean;
  @Input() formControl: FormControl;
  @Input() config: any;

  // Form controls for question and answer
  questionControl = new FormControl();
  answerControl = new FormControl();
  qaData: QaData;
  activeLanguageCode = DEFAULT_ACTIVE_LANGUAGE;
  private languageChanges$?: Observable<unknown>;
  textValueDef: any = { type: 'string', ui: { component: 'text-value-input', args: { activeLanguageCode: this.activeLanguageCode } } };


  constructor(private notificationService: NotificationService) {}

  ngOnInit() {
    const args = (this.config as any)?.ui?.args ?? {};
    if (typeof args.activeLanguageCode === 'string' && args.activeLanguageCode) {
      this.activeLanguageCode = args.activeLanguageCode;
    }
    if (args?.languageChanges$ && typeof args.languageChanges$.subscribe === 'function') {
      this.languageChanges$ = args.languageChanges$ as Observable<unknown>;
    }
    this.textValueDef = {
      ...this.textValueDef,
      ui: {
        ...this.textValueDef.ui,
        args: {
          activeLanguageCode: this.activeLanguageCode,
          languageChanges$: this.languageChanges$,
        },
      },
    };
    const defaultQaData: QaData = {
      question: { format: 'plain', i18n: { [this.activeLanguageCode]: '' } },
      answer: { format: 'plain', i18n: { [this.activeLanguageCode]: '' } },
    };
    this.qaData = safeJSONParse<QaData>(
      this.formControl.value,
      this.notificationService,
      defaultQaData,
    )!;
    this.initializeFormControls();
  }

  ngAfterViewInit() {
    // Subscribe to value changes for the question and answer controls
    this.questionControl.valueChanges.subscribe(this.onQuestionChange);
    this.answerControl.valueChanges.subscribe(this.onAnswerChange);
  }

  // Initialize form controls with values from qaData
  private initializeFormControls() {
    this.questionControl.setValue(this.toTextValueJson(this.qaData?.question));
    this.answerControl.setValue(this.toTextValueJson(this.qaData?.answer));
  }

  // Value change handler for the question field
  private onQuestionChange = (value: string) => {
    const next = this.parseTextValue(value);
    if (JSON.stringify(this.qaData?.question) !== JSON.stringify(next)) {
      this.qaData!.question = next;
      this.updateJsonString();
    }
  }

  // Value change handler for the answer field
  private onAnswerChange = (value: string) => {
    const next = this.parseTextValue(value);
    if (JSON.stringify(this.qaData?.answer) !== JSON.stringify(next)) {
      this.qaData!.answer = next;
      this.updateJsonString();
    }
  }

  private toTextValueJson(raw: any): string {
    if (raw && typeof raw === "object" && raw.i18n) return JSON.stringify(raw);
    return JSON.stringify({ format: "plain", i18n: { [this.activeLanguageCode]: raw ?? "" } });
  }

  private parseTextValue(raw: string | null | undefined): any {
    if (!raw) return { format: "plain", i18n: {} };
    try { return JSON.parse(raw); } catch { return { format: "plain", i18n: {} }; }
  }

  // Update the JSON string and form control value
  private updateJsonString() {
    const jsonString = JSON.stringify(this.qaData);
    this.formControl.setValue(jsonString);
    this.formControl.markAsDirty();
  }
}
